import {Component, EventEmitter, Input, Output, TemplateRef} from '@angular/core';
import {IScrollDataInput, ScrollingTypeEnum} from '../../models';

/**
 * ### SharedScrollingComponent
 * A reusable, adaptive scrolling container that supports either infinite scrolling or virtual scrolling,
 * determined by the `scrollType` input.
 *
 * This component delegates actual rendering to either:
 * - `app-shared-scrolling-infinite` (infinite scrolling mode)
 * - `app-shared-scrolling-virtual` (virtual scrolling mode)
 *
 * It passes down the necessary inputs and projects content for no-data and loading states.
 *
 * @interface SharedScrollingComponent
 *
 * @description
 * This component abstracts scroll type differences and exposes a unified interface.
 *
 * - When `scrollType` is `INFINITE`:
 *   - Uses pixel distance from bottom (`threshold`) to trigger loading more data.
 *   - Does NOT use `itemHeight`.
 * - When `scrollType` is `VIRTUAL`:
 *   - Uses `threshold` as an item count to trigger loading more data.
 *   - Requires `itemHeight` to calculate virtual scroll viewport item size.
 *
 * Emits `scrolledToThreshold` when the user scrolls near the end based on the type-specific threshold.
 *
 * Supports content projection for:
 * - `[noData]` — placeholder when there is no data.
 * - `[scrollLoader]` — loading indicator while data is fetching.
 *
 * @example
 * ```html
 * <app-shared-scrolling
 *   [scrollType]="scrollingTypeEnum.INFINITE"
 *   [scrollData]="myScrollData"
 *   [itemTemplate]="myItemTemplate"
 *   [threshold]="150"
 *   (scrolledToThreshold)="loadMore()"
 * >
 *   <ng-template noData>
 *     <p>No data found.</p>
 *   </ng-template>
 *   <ng-template scrollLoader>
 *     <mat-spinner></mat-spinner>
 *   </ng-template>
 * </app-shared-scrolling>
 * ```
 *
 * @input {string} idProperty - Name of the unique identifier property in the data items. Default `'id'`.
 * @input {boolean} isScrollDataLoading - Indicates if the scroll data is loading; controls loading placeholders.
 * @input {number | undefined} itemHeight - Height of each item in pixels. Required only for virtual scrolling.
 * @input {TemplateRef<unknown>} itemTemplate - Template for rendering each item.
 * @input {number} threshold - Scroll threshold for triggering load:
 *   - pixels from bottom for infinite scrolling,
 *   - number of remaining items for virtual scrolling.
 * @input {IScrollDataInput<unknown>} scrollData - Scroll data input including array of items.
 * @input {ScrollingTypeEnum} scrollType - Type of scrolling behavior. Defaults to `INFINITE`.
 *
 * @output {EventEmitter<void>} scrolledToThreshold - Event emitted when scroll reaches the threshold.
 *
 * @contentChild noData - Content projected when no data is present.
 * @contentChild scrollLoader - Content projected when data is loading.
 */
@Component({
    selector: 'app-shared-scrolling',
    templateUrl: './shared-scrolling.component.html',
    styleUrls: ['./shared-scrolling.component.scss'],
    standalone: false
})
export class SharedScrollingComponent {

  /**
   * Name of the property that uniquely identifies each list item.
   * @type {string}
   * @default 'id'
   * @example 'userId'
   */
  @Input() idProperty = 'id';

  /**
   * Flag to indicate if the scroll data is currently loading.
   * @type {boolean}
   * @default false
   */
  @Input() isScrollDataLoading = false;

  /**
   * Height in pixels of each item.
   * Only relevant when using virtual scrolling.
   * @type {number | undefined}
   */
  @Input() itemHeight?: number;

  /**
   * Template reference for rendering each scroll item.
   * @type {TemplateRef<unknown>}
   */
  @Input() itemTemplate!: TemplateRef<unknown>;

  /**
   * Threshold for triggering scroll load event.
   * - For INFINITE scroll: pixels from bottom to trigger load.
   * - For VIRTUAL scroll: number of items remaining to trigger load.
   * @type {number}
   */
  @Input() threshold!: number;

  /**
   * When false, the infinite scroller will not auto-request more rows when the list is shorter than the panel.
   */
  @Input() enableAutoFillWhenShort = true;

  /**
   * Bumped when list content changes without replacing `scrollData`.
   */
  @Input() scrollDataRevision = 0;

  /**
   * Input scroll data containing the array of items and optional metadata.
   * @type {IScrollDataInput<unknown>}
   */
  @Input() scrollData!: IScrollDataInput<unknown>;

  /**
   * Type of scrolling behavior.
   * Defaults to infinite scrolling.
   * @type {ScrollingTypeEnum}
   * @default ScrollingTypeEnum.INFINITE
   */
  @Input() scrollType: ScrollingTypeEnum = ScrollingTypeEnum.INFINITE;

  /**
   * Event emitted when scrolling reaches the defined threshold.
   * Triggers loading of more data or other scroll-related actions.
   * @type {EventEmitter<void>}
   */
  @Output() readonly scrolledToThreshold: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Enum reference for template use and comparisons.
   * @protected
   */
  protected readonly scrollingTypeEnum = ScrollingTypeEnum;


  get data(): unknown[] {
    return Array.isArray(this.scrollData?.data) ? this.scrollData.data as unknown[] : [];
  }

  /**
   * Returns the current scroll type.
   * @readonly
   * @type {ScrollingTypeEnum}
   */
  get scrollingType(): ScrollingTypeEnum {
    return this.scrollType;
  }

  /**
   * Called by child components when scroll reaches the threshold.
   * Emits the `scrolledToThreshold` event.
   * @returns {void}
   */
  scrollTriggered(): void {
    this.scrolledToThreshold.emit();
  }


}
